import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Brain, CaretLeft, CaretRight, ChatCircleText, Sparkle, X, Quotes, Share, Check } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useConsultModal } from "./ConsultModal";

const cards = [
  { num: "01", category: "Focus & Attention", front: "/cards/1.webp", back: "/cards/2.webp",
    quote: "I'm trying to focus… but I start scrolling unknowingly.",
    response: "This is not a lack of willpower! Your brain is drawn to new & stimulating content, which is why it can be difficult to stop scrolling even when you intend to focus." },
  { num: "02", category: "Overthinking Loops", front: "/cards/3.webp", back: "/cards/4.webp",
    quote: "I keep thinking about it again & again… I can't switch it off.",
    response: "When something feels unresolved, the mind tends to go back to it repeatedly to find an answer. This cycle of repetitive thinking is very common & can be tiring." },
  { num: "03", category: "Sleep & Racing Mind", front: "/cards/5.webp", back: "/cards/6.webp",
    quote: "My thoughts start running as soon as my head hits the pillow.",
    response: "When the day quietens down & there are fewer distractions, the mind often begins processing everything that was set aside during the day. This is very common." },
  { num: "04", category: "Getting Started", front: "/cards/7.webp", back: "/cards/8.webp",
    quote: "I know what to do… I just don't act on it.",
    response: "Starting isn't a discipline problem. Your brain weighs effort, reward and uncertainty before it lets you begin, that's why the start often feels heaviest." },
  { num: "05", category: "Anxiety & Restlessness", front: "/cards/9.webp", back: "/cards/10.webp",
    quote: "I feel anxious when I don't have anything to do.",
    response: "Stillness can feel unsafe to a nervous system that's used to staying busy. We work with the body & the breath to teach safety in slowness." },
  { num: "06", category: "Low Mood & Burnout", front: "/cards/11.webp", back: "/cards/12.webp",
    quote: "I feel sad, numb, tired & lost all the time.",
    response: "Burnout sits at the intersection of body, mind & meaning. Restoring it is rarely about rest alone, it's about recovering capacity, motivation and identity." },
  { num: "07", category: "Knowing You Need Help", front: "/cards/13.webp", back: "/cards/14.webp",
    quote: "I think I need help, I just don't know where to start.",
    response: "You don't have to have it figured out. A free 20-minute conversation with Shreeja is the gentlest way to find your next step." },
];

// All cards straight (no fan rotation), only a staggered load-in animation.
const fanLayout = Array.from({ length: 7 }, () => ({ rot: 0, dy: 0 }));

const FanCard = ({ card, layout, onOpen, isHovered, setHovered, idx }) => (
  <motion.button
    className={`fan-card ${isHovered ? "fan-card-hover" : ""}`}
    style={{ zIndex: isHovered ? 50 : 10 + idx }}
    initial={{ y: 48, opacity: 0, scale: 0.92 }}
    animate={{ y: layout.dy, opacity: 1, scale: 1 }}
    transition={{ delay: 0.08 * idx, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    onClick={onOpen}
    onMouseEnter={() => setHovered(idx)}
    onMouseLeave={() => setHovered(null)}
    onFocus={() => setHovered(idx)}
    onBlur={() => setHovered(null)}
    aria-label={`Card ${card.num} ${card.category}`}
    data-testid={`fan-card-${card.num}`}
  >
    <div className="fan-card-inner">
      <div className="fan-card-header">
        <span className="fan-card-num">#{card.num}</span>
      </div>
      <div className="fan-card-img-wrap">
        <img src={card.front} alt={card.category} className="fan-card-img" loading="lazy" />
      </div>
      <div className="fan-card-footer">
        <span className="fan-card-cat">{card.category}</span>
        <span className="fan-card-open">Open <ArrowRight size={11} weight="bold" /></span>
      </div>
    </div>
  </motion.button>
);

const Popup = ({ index, setIndex, onClose }) => {
  const card = cards[index];
  const [copied, setCopied] = useState(false);
  const { open: openConsult } = useConsultModal();
  const go = useCallback((dir) => setIndex((i) => (i + dir + cards.length) % cards.length), [setIndex]);

  // eslint-disable-next-line
  useEffect(() => { setCopied(false); }, [index]);

  // Reflect current card in URL for deep-linking (replaceState, no navigation)
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("card", card.num);
    window.history.replaceState(null, "", url.toString());
    return () => {
      const u = new URL(window.location.href);
      u.searchParams.delete("card");
      window.history.replaceState(null, "", u.pathname + (u.search ? u.search : "") + u.hash);
    };
  }, [card.num]);

  const onShare = async () => {
    const url = new URL(window.location.origin + "/");
    url.searchParams.set("card", card.num);
    const shareData = {
      title: `MindDhara · ${card.category}`,
      text: `"${card.quote}", see how MindDhara works with this.`,
      url: url.toString(),
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url.toString());
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch (e) {
      console.warn("[FlipCards] first clipboard attempt failed, retrying:", e?.message || e);
      try {
        await navigator.clipboard.writeText(url.toString());
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch (e2) {
        console.error("[FlipCards] clipboard unavailable:", e2?.message || e2);
      }
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  return (
    <div className="popup-overlay" onClick={onClose} data-testid="flipcard-popup">
      <button className="popup-close" onClick={onClose} aria-label="Close" data-testid="flipcard-popup-close">
        <X size={20} weight="bold" />
      </button>
      <button className="popup-nav popup-nav-prev" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous" data-testid="flipcard-popup-prev">
        <CaretLeft size={22} weight="bold" />
      </button>

      <div className="popup-shell" onClick={(e) => e.stopPropagation()} data-testid="flipcard-popup-card">
        <div className="hero-fw-strip">
          <h2 className="hero-fw-title">#{card.num} · {card.category}</h2>
          <div className="hero-fw-strip-right">
            <button onClick={onShare} className={`popup-share ${copied ? "copied" : ""}`} aria-label="Share this card" data-testid="flipcard-popup-share">
              {copied ? (<><Check size={14} weight="bold" /> Link copied</>) : (<><Share size={14} weight="bold" /> Share Card {card.num}</>)}
            </button>
            <span className="hero-fw-eyebrow">Card {String(index + 1).padStart(2, "0")} / {String(cards.length).padStart(2, "0")}</span>
          </div>
        </div>

        <div className="hero-fw-body" key={card.num}>
          <div className="hero-fw-pane hero-fw-pane-q">
            <span className="hero-fw-pane-label"><ChatCircleText size={14} weight="duotone" /> What people say</span>
            <div className="hero-fw-pane-img-wrap"><img src={card.front} alt={card.category} className="hero-fw-pane-img" /></div>
          </div>
          <div className="hero-fw-divider" aria-hidden>
            <div className="hero-fw-divider-line" />
            <div className="hero-fw-divider-arrow"><ArrowRight size={22} weight="bold" /></div>
            <div className="hero-fw-divider-line" />
          </div>
          <div className="hero-fw-pane hero-fw-pane-a">
            <span className="hero-fw-pane-label hero-fw-pane-label-a"><Sparkle size={14} weight="fill" /> MindDhara&apos;s response</span>
            <div className="hero-fw-pane-img-wrap"><img src={card.back} alt={`${card.category} response`} className="hero-fw-pane-img" /></div>
          </div>
        </div>

        <div className="hero-fw-footer popup-footer-split">
          <div className="popup-footer-left">
            <span className="md-label-eyebrow" style={{ color: "var(--md-secondary-dark)" }}>A simple promise</span>
            <span className="popup-footer-tagline">Become Your Own <span className="italic">Brain-Manager</span></span>
          </div>
          <div className="popup-footer-center">
            <div className="hero-fw-pages">
              {cards.map((c, i) => (
                <button key={c.id || c.slug || `dot-${i}`} className={`hero-fw-dot ${i === index ? "active" : ""}`} onClick={() => setIndex(i)} aria-label={`Card ${i + 1}`} data-testid={`popup-dot-${i}`} />
              ))}
            </div>
          </div>
          <div className="popup-footer-right">
            <button onClick={() => { onClose(); openConsult(); }} className="btn-primary text-sm" data-testid="popup-cta">Book a Free Consultation <ArrowRight size={14} weight="bold" /></button>
            <Link to="/therapists" className="btn-outline text-sm" data-testid="popup-cta-browse">Browse Therapists</Link>
          </div>
        </div>
      </div>

      <button className="popup-nav popup-nav-next" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next" data-testid="flipcard-popup-next">
        <CaretRight size={22} weight="bold" />
      </button>
    </div>
  );
};

export default function FlipCards() {
  const { open: openConsult } = useConsultModal();
  const [openIndex, setOpenIndex] = useState(() => {
    // Deep-link: if URL has ?card=NN, auto-open that card
    if (typeof window === "undefined") return null;
    const num = new URLSearchParams(window.location.search).get("card");
    if (!num) return null;
    const idx = cards.findIndex((c) => c.num === num.padStart(2, "0"));
    return idx >= 0 ? idx : null;
  });
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    document.body.style.overflow = openIndex !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [openIndex]);

  return (
    <section className="hero-fan" data-testid="flip-cards-section">
      <div className="hero-leaves" aria-hidden>
        {/* Floating brain glyphs — clean vector, no background or border */}
        <Brain className="hero-leaf hero-leaf-1 hero-brain-badge" weight="duotone" />
        <Brain className="hero-leaf hero-leaf-2 hero-brain-badge" weight="duotone" />
        <Brain className="hero-leaf hero-leaf-3 hero-brain-badge" weight="duotone" />
      </div>
      <div className="md-container">
        <div className="hero-fan-intro">
          <h1 className="font-serif leading-[1.05] mb-3" style={{ fontSize: "2.5rem" }}>
            How Is Your <span style={{ color: "var(--md-primary)" }}>Brain Today?</span>
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: "var(--md-text-soft)" }}>
            Every thought, emotion, habit, and challenge starts somewhere.{" "}
            <strong style={{ color: "var(--md-text)" }}>
              Choose the card that feels closest to your current experience.
            </strong>
          </p>
        </div>

        <div className="hero-fan-deck" data-testid="hero-fan-deck">
          {cards.map((c, i) => (
            <FanCard key={c.num} card={c} layout={fanLayout[i]} idx={i} isHovered={hovered === i} setHovered={setHovered} onOpen={() => setOpenIndex(i)} />
          ))}
        </div>

        <div className="hero-fan-cta">
          <div className="hero-fan-tagline">
            <span className="hero-fan-tagline-bold">Become Your Own <span className="italic">Brain-Manager</span></span>
          </div>
          <div className="hero-fan-ctas">
            <button onClick={openConsult} className="btn-primary text-sm" data-testid="hero-fan-consult">
              Book a Free Consultation <ArrowRight size={14} weight="bold" />
            </button>
            <Link to="/therapists" className="btn-outline text-sm" data-testid="hero-fan-browse">
              Browse Therapists
            </Link>
            <Link to="/mentors" className="btn-outline text-sm" data-testid="hero-fan-browse-mentors">
              Browse Mentors
            </Link>
          </div>
        </div>
      </div>

      {openIndex !== null && <Popup index={openIndex} setIndex={setOpenIndex} onClose={() => setOpenIndex(null)} />}
    </section>
  );
}
